import React from 'react';

import { concat, of, mergeMap, from } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, take } from 'rxjs/operators';
import { FormattedMessage } from 'react-intl';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import * as Actions from './bulk.reship.actions';
import { BULK_RESHIP_JOB_COMPLETE } from '../../../services/pusher-service/pusher-contract-messages';
import { bulkloadFulfillmentPlans } from '../../bulkload/bulkload.actions';
import { silentRequestGetOrders } from '../../orders/orders.actions';
import { messages } from './messages';
import { getIsJobComplete } from '../update/pusher-helpers';
import { bulkReship, getBulkJobData } from '../update/bulk.update.api';
import { createPusherMessageCache } from '../../pusher/message-cache';

const inProgressNotification = {
  title: <FormattedMessage {...messages.reshipInProgress} />,
  notificationType: NOTIFICATION_TYPE_INFO,
  duration: NOTIFICATION_DURATIONS.MEDIUM,
};

const successNotification = {
  title: <FormattedMessage {...messages.reshipSuccessNotificationTitle} />,
  content: <FormattedMessage {...messages.reshipSuccessNotificationContent} />,
  notificationType: NOTIFICATION_TYPE_SUCCESS,
  duration: NOTIFICATION_DURATIONS.SHORT,
};

const errorNotification = {
  title: <FormattedMessage {...messages.reshipFailureNotificationTitle} />,
  content: <FormattedMessage {...messages.reshipFailureNotificationContent} />,
  notificationType: NOTIFICATION_TYPE_ERROR,
  duration: NOTIFICATION_DURATIONS.SHORT,
};

export const bulkReshipStart = (action$) =>
  action$.pipe(
    ofType(Actions.BULK_RESHIP_START),
    mergeMap(({ fulfillmentPlanIds, trackReshipShipmentSuccess, trackReshipShipmentError }) => {
      const inProgressNotificationId = NotificationService.showNotification(inProgressNotification);

      const { findMessage } = createPusherMessageCache([BULK_RESHIP_JOB_COMPLETE]);

      return getBulkJobData({ fulfillmentPlanIds }).pipe(
        mergeMap((bulkJobData) => {
          const { bulkUpdateJobGuid } = bulkJobData;
          const bulkReship$ = from(bulkReship(bulkUpdateJobGuid));

          return bulkReship$.pipe(
            mergeMap((bulkReshipResponse) => {
              const jobGuid = bulkReshipResponse.data.jobGuids[0];
              const messageChecker = (pusherMessage) => pusherMessage.messageGuid === jobGuid && getIsJobComplete(pusherMessage);

              return findMessage(messageChecker).pipe(
                take(1),
                mergeMap((pusherMessage) => {
                  trackReshipShipmentSuccess();
                  NotificationService.removeNotification(inProgressNotificationId);

                  const notification = pusherMessage?.data?.succeeded ? successNotification : errorNotification;

                  NotificationService.showNotification(notification);

                  return concat(of(bulkloadFulfillmentPlans(fulfillmentPlanIds)), of(silentRequestGetOrders()));
                }),
              );
            }),
          );
        }),
        catchError(() => {
          trackReshipShipmentError();
          NotificationService.showNotification(errorNotification);
        }),
      );
    }),
  );
